// src/utils/useResourcePermissions.js
// ─────────────────────────────────────────────────────────────────────────────
// Hook que resuelve los permisos CRUD granulares del usuario sesionado sobre
// un recurso específico, identificado por su NOMBRE en SEGURIDAD.Recursos.
//
// Uso:
//   const { puedeCrear, puedeEditar, puedeEliminar, loading } = useResourcePermissions('Personas');
//
// Reglas:
//   • SuperAdmin (idPerfil === 1) → todo true, sin llamadas API.
//   • Resultados en caché (sessionStorage) durante la sesión para evitar
//     múltiples peticiones al navegar entre catálogos.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import { getSessionUserId } from './SessionUser';

const CACHE_KEY_RRP = 'sk_rrp_cache';
const CACHE_KEY_ROLES = 'sk_user_roles_cache';
const CACHE_KEY_RES = 'sk_recursos_cache';
const CACHE_KEY_PERM = 'sk_permisos_cache';

// Mapeo canónico de nombres de permisos (en mayúsculas) a la clave del resultado
const PERMISO_MAP = {
  VER: 'puedeVer',
  CREAR: 'puedeCrear',
  EDITAR: 'puedeEditar',
  ELIMINAR: 'puedeEliminar',
  EXPORTAR: 'puedeExportar',
  IMPORTAR: 'puedeImportar',
};

const DEFAULT_PERMISOS = {
  puedeVer: false,
  puedeCrear: false,
  puedeEditar: false,
  puedeEliminar: false,
  puedeExportar: false,
  puedeImportar: false,
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

// ─── Helpers de caché ────────────────────────────────────────────────────────
function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Soporte para entradas con TTL: { data, ts }
    if (parsed && typeof parsed === 'object' && 'data' in parsed && 'ts' in parsed) {
      if (Date.now() - parsed.ts > CACHE_TTL_MS) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.data;
    }
    // Compatibilidad con entradas antiguas sin TTL
    sessionStorage.removeItem(key);
    return null;
  } catch { return null; }
}

function writeCache(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data: value, ts: Date.now() }));
  } catch { /* quota exceeded */ }
}

// ─── Loader con caché ────────────────────────────────────────────────────────
async function fetchWithCache(cacheKey, url) {
  const cached = readCache(cacheKey);
  // Si tenemos caché y tiene datos, lo usamos.
  if (cached && Array.isArray(cached) && cached.length > 0) return cached;

  try {
    const res = await axios.get(`${API_BASE}${url}`);
    const data = res.data || [];
    if (data.length > 0) {
      writeCache(cacheKey, data);
    }
    return data;
  } catch (err) {
    console.error(`[fetchWithCache] Error fetching ${url}:`, err);
    return cached || [];
  }
}

// ─── Hook principal ───────────────────────────────────────────────────────────
export function useResourcePermissions(nombreRecursoExplicito) {
  const [permisos, setPermisos] = useState(DEFAULT_PERMISOS);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      // SuperAdmin ya no tiene bypass automático, debe regirse por la matriz RRP

      const idUsuario = getSessionUserId();
      // Permitimos que nombreRecursoExplicito sea indefinido para inferirlo
      if (!idUsuario) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        // ── 1. Cargar catálogos en paralelo (con caché) ───────────────────
        // NOTA: RolesRecursosPermisos se carga SIEMPRE fresco (sin caché) para
        // reflejar cambios en la Matriz de Permisos de forma inmediata.
        const rrpFresh = axios.get(`${API_BASE}/RolesRecursosPermisos`).then(r => r.data || []);
        const [rawRrp, rawRoles, rawRecursos, rawPermisos, rawMenus] = await Promise.all([
          rrpFresh,
          fetchWithCache(`${CACHE_KEY_ROLES}_${idUsuario}`, `/UsuarioRol/Usuario/${idUsuario}`),
          fetchWithCache(CACHE_KEY_RES, '/Recursos'),
          fetchWithCache(CACHE_KEY_PERM, '/Permisos'),
          fetchWithCache('sk_all_menus_cache', '/Menus'),
        ]);

        console.log('[useResourcePermissions] rawRoles recuperados:', rawRoles);
        console.log('[useResourcePermissions] Cantidad RRP cargados:', rawRrp.length);

        let targetResourceName = nombreRecursoExplicito;

        // ── 1.5. Inferir el nombre del recurso desde la URL y el menú ──
        if (!targetResourceName) {
          const currentPath = location.pathname.toLowerCase().replace(/\/$/, "");
          const query = new URLSearchParams(location.search);
          const dynamicPath = query.get("path");

          const normalize = (p) => (p || "").replace(/\/+/g, "/").replace(/\/$/, "").replace(/^\//, "").toLowerCase().trim();
          const currentFull = currentPath === "/menu/dynamic" ? normalize(dynamicPath) : normalize(currentPath);

          let bestMatch = null;
          let maxLen = -1;

          for (const node of rawMenus) {
            const nodeClave = String(node.clave ?? node.Clave ?? "").trim();
            const nodeRoute = String(node.ruta ?? node.Ruta ?? "").toLowerCase().trim();
            const menuPath = normalize(`${nodeRoute}${nodeClave}`);

            if (nodeRoute.includes("/navigate") && nodeClave.startsWith("/")) {
              if (currentPath === nodeClave.toLowerCase()) {
                bestMatch = node;
                break;
              }
            }

            if (currentFull && menuPath) {
              if (currentFull === menuPath) {
                bestMatch = node;
                break;
              }
              if (currentFull.startsWith(menuPath) && menuPath.length > 0) {
                if (menuPath.length > maxLen) {
                  maxLen = menuPath.length;
                  bestMatch = node;
                }
              }
            }
          }

          if (bestMatch) {
            targetResourceName = bestMatch.nombre ?? bestMatch.Nombre;
          }
        }

        if (!targetResourceName) {
          if (!cancelled) setLoading(false);
          return;
        }

        // ── 1.5 Mapeos explícitos (hardcoded) para discrepancias fuertes ──
        const RESOURCE_MAPPINGS = {
          'Configuraciones Sistema': 'SISTEMA_CONFIGURACIONES'
        };

        if (RESOURCE_MAPPINGS[targetResourceName]) {
          console.log(`[useResourcePermissions] Remapeando explícitamente: ${targetResourceName} -> ${RESOURCE_MAPPINGS[targetResourceName]}`);
          targetResourceName = RESOURCE_MAPPINGS[targetResourceName];
        }


        // ── 2. Resolver IdRecurso buscando por nombre (case-insensitive y flexible) ──
        const normalizeName = (n) => {
          if (!n) return '';
          return n.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
            .replace(/catálogo de /g, '')
            .replace(/catalogo de /g, '')
            .replace(/ de la plataforma/g, '') // Mapeo flexible para "Configuraciones de la Plataforma" -> "Configuraciones"
            .replace(/\s+/g, '') // Eliminar todos los espacios para evitar discrepancias de espaciado
            .replace(/de/g, '')  // Eliminar preposición "de" para emparejar "Avisos de Privacidad" (avisosprivacidad) y "AvisosPrivacidad"
            .trim();
        };
        const targetStr = normalizeName(targetResourceName);

        // ── Prioridad 0: Coincidencia exacta case-insensitive (sin normalización agresiva) ──
        // Esto evita que 'Cursos' y 'Catálogo de Cursos' resuelvan al mismo string
        // y el .find() devuelva el incorrecto por orden de array.
        const exactLower = targetResourceName.trim().toLowerCase();
        let recurso = rawRecursos.find(r =>
          (r.nombre ?? r.Nombre ?? '').trim().toLowerCase() === exactLower
        );

        // ── Prioridad 1: Coincidencia normalizada exacta ──
        if (!recurso) {
          recurso = rawRecursos.find(r => normalizeName(r.nombre ?? r.Nombre) === targetStr);
        }

        // ── Prioridad 2: Coincidencia normalizada fuzzy ──
        if (!recurso && targetStr.length > 0) {
          recurso = rawRecursos.find(r => {
            const rName = normalizeName(r.nombre ?? r.Nombre);
            return rName.length > 0 && (rName.includes(targetStr) || targetStr.includes(rName));
          });
        }

        // ── Prioridad 3: Si se pasó nombre explícito y no se halló en caché, forzar recarga fresca ──
        if (!recurso && nombreRecursoExplicito) {
          try {
            const freshRes = await axios.get(`${API_BASE}/Recursos`).then(r => r.data || []);
            if (freshRes.length > 0) {
              writeCache(CACHE_KEY_RES, freshRes);
              // Repetir las tres prioridades sobre datos frescos
              recurso =
                freshRes.find(r => (r.nombre ?? r.Nombre ?? '').trim().toLowerCase() === exactLower) ||
                freshRes.find(r => normalizeName(r.nombre ?? r.Nombre) === targetStr) ||
                freshRes.find(r => {
                  const rName = normalizeName(r.nombre ?? r.Nombre);
                  return rName.length > 0 && (rName.includes(targetStr) || targetStr.includes(rName));
                });
            }
          } catch (e) {
            console.error('[useResourcePermissions] Fallo al re-consultar Recursos frescos:', e);
          }
        }

        console.log('[useResourcePermissions] Inferido:', targetResourceName, '-> Recurso DB:', recurso?.nombre ?? recurso?.Nombre);

        if (!recurso) {
          if (!cancelled) setLoading(false);
          return;
        }
        const idRecurso = recurso.idRecurso ?? recurso.IdRecurso;

        // ── 3. Obtener los IdRol del usuario ──────────────────────────────
        // REGLA: Todos los usuarios (incluyendo SuperAdmin) se evalúan SOLO
        // con sus roles reales asignados en UsuarioRol. Para darle permisos
        // al SuperAdmin, configúralos en la Matriz para uno de sus roles asignados.
        const idRolesUsuario = new Set(
          rawRoles.map(r => r.idRol ?? r.IdRol)
        );

        console.log('[useResourcePermissions] Roles del usuario:', Array.from(idRolesUsuario));

        // ── 4. Mapa de IdPermiso → nombre canónico ────────────────────────
        const permisoNombreMap = new Map();
        rawPermisos.forEach(p => {
          const id = p.idPermiso ?? p.IdPermiso;
          const nom = (p.nombre ?? p.Nombre ?? '').trim().toUpperCase();
          if (id) permisoNombreMap.set(id, nom);
        });

        // ── 5. Filtrar RRP: mismo recurso + un rol del usuario ────────────
        const resultado = { ...DEFAULT_PERMISOS };

        let foundAnyRrp = false;

        rawRrp.forEach(rrp => {
          const idRec = rrp.idRecurso ?? rrp.IdRecurso;
          const idRol = rrp.idRol ?? rrp.IdRol;
          const idPer = rrp.idPermiso ?? rrp.IdPermiso;

          if (Number(idRec) !== Number(idRecurso)) return;
          if (!idRolesUsuario.has(idRol) && !idRolesUsuario.has(Number(idRol))) return;

          foundAnyRrp = true;

          const nombrePermiso = permisoNombreMap.get(idPer) ?? permisoNombreMap.get(Number(idPer));
          const clave = nombrePermiso ? PERMISO_MAP[nombrePermiso] : null;

          console.log('[useResourcePermissions] MATCH RRP:', { idRec, idRol, idPer, nombrePermiso, clave });

          if (clave) resultado[clave] = true;
          if (clave) resultado.puedeVer = true;
        });

        console.log('[useResourcePermissions] RRP encontrados para el recurso y roles?:', foundAnyRrp);
        console.log('[useResourcePermissions] Permisos finales:', resultado);

        if (!cancelled) setPermisos(resultado);
      } catch (err) {
        console.error('[useResourcePermissions] Error al resolver permisos:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [nombreRecursoExplicito, location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...permisos, loading };
}

// ─── Utilidad para invalidar caché (usar al hacer logout o al actualizar permisos) ────
export function clearPermissionsCache() {
  ['sk_rrp_cache', 'sk_recursos_cache', 'sk_permisos_cache', 'sk_all_menus_cache', 'sk_all_roles_cache'].forEach(k => sessionStorage.removeItem(k));
  // Los roles por usuario también se limpian
  const keys = Object.keys(sessionStorage).filter(k => k.startsWith('sk_user_roles_cache'));
  keys.forEach(k => sessionStorage.removeItem(k));
  console.log('[useResourcePermissions] Caché de permisos limpiado.');
}

// Exponer en window para poder limpiar desde la consola del navegador en dev
if (typeof window !== 'undefined') {
  window.__clearPermissionsCache = clearPermissionsCache;
}
